
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { UserNav } from "./UserNav";
import { OrganizationSelector } from "./organizations/OrganizationSelector";
import { GlobalSearch } from "./GlobalSearch";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  NavigationMenu, 
  NavigationMenuItem, 
  NavigationMenuList,
  NavigationMenuLink,
  navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Header = () => {
  const { userRole } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isActive = (path: string) => location.pathname === path;
  
  const navigationItems = [
    { path: "/", label: "Dashboard" },
    { path: "/leads", label: "Leads" },
    { path: "/contacts", label: "Contacts" },
    { path: "/tasks", label: "Tasks" },
    { path: "/project-dashboard", label: "Projects" },
  ];
  
  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };
  
  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        {/* Mobile menu button */}
        {isMobile && (
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="mr-2 md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>
                  <img 
                    src="/lovable-uploads/d3c178bd-e905-4e9f-aea6-25fff0b49ef5.png" 
                    alt="Domotai" 
                    className="h-8 w-auto"
                  />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleMobileNavClick}
                    className={cn(
                      "block px-3 py-2 rounded-md text-base font-medium transition-colors",
                      isActive(item.path)
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="pt-4 border-t">
                  <GlobalSearch />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 mr-4">
          <img 
            src="/lovable-uploads/d3c178bd-e905-4e9f-aea6-25fff0b49ef5.png" 
            alt="Domotai" 
            className="h-8 w-auto"
          />
        </Link>

        {/* Desktop navigation */}
        <div className="mr-4 hidden md:flex">
          <NavigationMenu>
            <NavigationMenuList>
              {navigationItems.map((item) => (
                <NavigationMenuItem key={item.path}>
                  <NavigationMenuLink 
                    asChild
                    className={cn(
                      navigationMenuTriggerStyle(),
                      isActive(item.path) && "bg-accent text-accent-foreground"
                    )}
                  >
                    <Link to={item.path}>{item.label}</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        
        <div className="flex flex-1 items-center justify-end space-x-2">
          <div className="hidden md:flex items-center space-x-2">
            <GlobalSearch />
          </div>
          <div className="flex items-center space-x-2">
            <OrganizationSelector />
            <UserNav />
          </div>
        </div>
      </div>
    </header>
  );
};
